import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "@/index.css";
import App from "@/App.tsx";
import "@/libs/i18n";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

const env = import.meta.env.MODE;

document.title =
  env !== "production"
    ? `${
        env.charAt(0).toUpperCase() + env.slice(1)
      } -  Service Trajet Santé de l’assurance maladie`
    : "Service Trajet Santé de l’assurance maladie";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENV,
  release: import.meta.env.VITE_APP_VERSION,
  debug: false,
  // Ignore React warnings and deprecation notices, and network errors
  ignoreErrors: [
    /Support for defaultProps will be removed/,
    /validateDOMNesting/,
    /does not recognize the .* prop on a DOM element/,
    /Failed to fetch/,
    /NetworkError/,
    /Network request failed/,
  ],
  denyUrls: [/wchat\.eu\.freshchat\.com/],
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      networkCaptureBodies: false,
      networkDetailAllowUrls: [],
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    /^localhost(:\d+)?$/, // matches localhost with optional port
    /\.tsam\.thetreep\.com$/, // matches any subdomain of tsam.thetreep.com
    /\.servicetrajetsanteassurancemaladie\.fr$/, // matches any subdomain of servicetrajetsanteassurancemaladie.fr
  ],
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
    <StrictMode>
      <App />
    </StrictMode>
  </Sentry.ErrorBoundary>,
);
